/**
 * Rueckspeisung: bewertete Gewerkepaare der Koordinierung -> Gefaehrdungsmatrix.
 *
 * ─── Warum ueberhaupt zwei Matrizen? ────────────────────────────────────────
 * Die bestehende Matrix (`src/components/HazardMatrixView.tsx`) arbeitet auf
 * den KONTAKTEN eines Berichts und ist der einzige Weg fuer Projekte OHNE
 * SiGe-Plan (Begehungs- und HSE-Projekte). Die Koordinierung arbeitet auf den
 * GEWERKEN des Plans und kennt Zeitraeume. Beide bleiben bestehen; diese Datei
 * ist die Bruecke von der zweiten in die erste — in EINE Richtung.
 * `report.mutualHazards` wird nicht migriert, nicht kopiert, nicht geloescht:
 * `generatorStandard.ts:529-551` gibt Altberichte aus `report_history` wieder,
 * und jede Datenmodellaenderung braeche deren Wiedergabe.
 *
 * ─── Warum reine Funktionen ─────────────────────────────────────────────────
 * Kein DOM, kein Supabase, kein React — stehende Entscheidung „duenner
 * Container, Logik in Hooks + pure Funktionen". Das Projekt hat keine
 * jsdom-Testumgebung; alles, was fachlich entscheidet, muss ohne sie pruefbar
 * sein.
 *
 * ─── Was hier NICHT passiert ────────────────────────────────────────────────
 * Nichts laeuft automatisch. Es gibt keinen Aufruf beim Laden und keinen beim
 * Speichern. Die Uebernahme geschieht ausschliesslich auf ausdruecklichen
 * Knopfdruck, und was uebernommen wurde, traegt in der Datenbank
 * `koordinierung_paare.uebernommen_matrix_am` — sonst wiederholte sie sich
 * stillschweigend und ueberschriebe von Hand nachgebesserte Zellen.
 */

import type { MutualHazard } from '../../types';
import type { KoordinierungPaar, Prioritaet } from './koordinierungLogic';

// ═══════════════════════════════════════════════════════════════════════════
// 1) ABBILDUNG DER FELDER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die dreistufige Prioritaet der Koordinierung auf die dreistufige Schwere der
 * Matrix. Beide Skalen haben drei Stufen und dieselbe Bedeutung — hier wird
 * nichts umgerechnet, nur uebersetzt.
 */
export const PRIORITAET_ZU_SEVERITY: Record<Prioritaet, MutualHazard['severity']> = {
    hoch: 'high',
    mittel: 'medium',
    niedrig: 'low',
};

/** Rangfolge fuer den Fall, dass mehrere Bewertungen in EINE Zelle fallen. */
export const SEVERITY_RANG: Record<MutualHazard['severity'], number> = {
    low: 1,
    medium: 2,
    high: 3,
};

const text = (wert: unknown): string => (typeof wert === 'string' ? wert.trim() : '');

/**
 * Welche Zeilen ueberhaupt zur Uebernahme anstehen.
 *
 * Vier Bedingungen, jede aus einem eigenen Grund:
 *  - `bestaetigt_am` gesetzt — nur was ein Mensch bestaetigt hat, geht in ein
 *    Dokument, das der SiGeKo verantwortet. Ein Regel- oder KI-Vorschlag ist
 *    dafuer keine Grundlage.
 *  - `uebernommen_matrix_am` leer — sonst ueberschriebe ein zweiter Klick
 *    stillschweigend, was in der Matrix inzwischen von Hand praezisiert wurde.
 *  - `veraltet_seit` leer — die Ueberschneidung besteht nicht mehr. Die Zeile
 *    bleibt als Nachweis erhalten, sie wandert nur nicht mehr weiter.
 *  - Gefaehrdungstext vorhanden — eine leere Zelle waere in der Matrix nicht
 *    von „noch nicht geprueft" zu unterscheiden.
 */
export const uebernahmeKandidaten = (paare: KoordinierungPaar[] | null | undefined): KoordinierungPaar[] =>
    (paare ?? []).filter(p =>
        !!p
        && !!text(p.bestaetigt_am)
        && !text(p.uebernommen_matrix_am)
        && !text(p.veraltet_seit)
        && !!text(p.gefaehrdung),
    );

// ═══════════════════════════════════════════════════════════════════════════
// 2) DIE NAMENSBRUECKE
// ═══════════════════════════════════════════════════════════════════════════
//
// Die Achsen der bestehenden Matrix heissen `Contact.company || role`
// (HazardMatrixView.tsx:26-31). Das Gewerk im Plan kommt aus dem Freitextfeld
// `Arbeitsschritt.gewerk`, dessen Datalist aus `Contact.tradeType` gespeist
// wird (ArbeitsschritteListe.tsx:636). Das sind ZWEI Namen fuer dieselbe
// Firma: „Elektro Meier GmbH" auf der Achse, „Elektro" im Plan.
//
// Ohne bewusste Zuordnung stuenden nach der Uebernahme beide in derselben
// Matrix — mit halber Bewertung je Name. Deshalb wird hier NICHT geraten:
// diese Datei liefert einen VORSCHLAG mit Herkunftsangabe, entschieden wird
// im Dialog. Geraten wird nur, was sich exakt (nach Vergleichsform) deckt;
// Teilzeichenketten sind ausdruecklich kein Treffer — „Bau" faende sonst
// „Baumpflege", und die Firmenzusammenfuehrung dieses Repos ist genau darueber
// schon einmal gestolpert.

/**
 * Vergleichsform: klein, ohne Umlaut-Eigenheiten, ohne Rechtsform, ohne
 * Sonderzeichen. Bewusst grob genug fuer „Elektro-Meier GmbH" vs.
 * „Elektro Meier", aber ohne jede Aehnlichkeitsrechnung.
 */
export const vergleichsform = (wert: string | null | undefined): string =>
    text(wert)
        .toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/\b(gmbh|mbh|ag|kg|ohg|gbr|ug|se|co|e\.?k\.?|und|&)\b/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

/** Eine Achse der bestehenden Matrix. */
export interface MatrixAchse {
    /** Anzeigename der Achse — `Contact.company || role`. */
    name: string;
    /** `Contact.tradeType` — die Taetigkeit, mit der der Plan arbeitet. */
    taetigkeit?: string | null;
}

/** Woher ein Vorschlag kommt. `offen` heisst: der Mensch muss entscheiden. */
export type ZuordnungHerkunft = 'name' | 'taetigkeit' | 'offen';

export interface Namensvorschlag {
    norm: string;
    label: string;
    /** Wieviele anstehende Bewertungen dieses Gewerk betrifft. */
    anzahl: number;
    /** Achsenname der Matrix — `null`, wenn nichts sicher passt. */
    vorschlag: string | null;
    herkunft: ZuordnungHerkunft;
}

/**
 * Alle Gewerke der anstehenden Paare, je `norm` einmal, mit Zuordnungs-
 * vorschlag. Reihenfolge: die haeufigsten zuerst, dann alphabetisch — der
 * Dialog zeigt oben, wo die meiste Arbeit haengt.
 */
export const schlageZuordnungVor = (
    paare: KoordinierungPaar[] | null | undefined,
    achsen: MatrixAchse[] | null | undefined,
): Namensvorschlag[] => {
    const kandidaten = uebernahmeKandidaten(paare);

    const gewerke = new Map<string, { label: string; anzahl: number }>();
    const merke = (norm: string, label: string): void => {
        const schluessel = text(norm);
        if (!schluessel) return;
        const vorhanden = gewerke.get(schluessel);
        if (vorhanden) {
            vorhanden.anzahl += 1;
            if (!vorhanden.label) vorhanden.label = text(label);
            return;
        }
        gewerke.set(schluessel, { label: text(label) || schluessel, anzahl: 1 });
    };
    kandidaten.forEach(p => {
        merke(p.gewerk_a_norm, p.gewerk_a_label);
        merke(p.gewerk_b_norm, p.gewerk_b_label);
    });

    // Zwei Nachschlagewerke. Mehrdeutigkeiten fallen heraus: passen zwei
    // Achsen auf denselben Text, ist keine davon ein sicherer Treffer.
    const nachName = new Map<string, string | null>();
    const nachTaetigkeit = new Map<string, string | null>();
    const eintragen = (karte: Map<string, string | null>, schluessel: string, name: string): void => {
        if (!schluessel) return;
        if (karte.has(schluessel) && karte.get(schluessel) !== name) karte.set(schluessel, null);
        else karte.set(schluessel, name);
    };
    (achsen ?? []).forEach(a => {
        const name = text(a?.name);
        if (!name) return;
        eintragen(nachName, vergleichsform(name), name);
        eintragen(nachTaetigkeit, vergleichsform(a?.taetigkeit), name);
    });

    return [...gewerke.entries()]
        .map(([norm, g]) => {
            // Der Anzeigename schlaegt die Taetigkeit: er ist der Name, unter
            // dem die Firma im Projekt gefuehrt wird. Die Taetigkeit ist die
            // Bruecke fuer den Regelfall, dass der Plan „Elektro" sagt und die
            // Achse „Elektro Meier GmbH" heisst.
            const perName = nachName.get(vergleichsform(g.label));
            if (perName) return { norm, label: g.label, anzahl: g.anzahl, vorschlag: perName, herkunft: 'name' as const };
            const perTaetigkeit = nachTaetigkeit.get(vergleichsform(g.label));
            if (perTaetigkeit) {
                return { norm, label: g.label, anzahl: g.anzahl, vorschlag: perTaetigkeit, herkunft: 'taetigkeit' as const };
            }
            return { norm, label: g.label, anzahl: g.anzahl, vorschlag: null, herkunft: 'offen' as const };
        })
        .sort((a, b) => (b.anzahl - a.anzahl) || a.label.localeCompare(b.label, 'de'));
};

/**
 * Entscheidung des Anwenders: `gewerk_norm` -> Achsenname der Matrix.
 * Ein fehlender oder leerer Eintrag heisst „nicht uebernehmen" — nie „nimm
 * den Wortlaut aus dem Plan". Wer den Wortlaut des Plans will, waehlt ihn im
 * Dialog ausdruecklich aus.
 */
export type Zuordnung = Record<string, string>;

/** Der Vorschlag als Ausgangsbelegung des Dialogs — offene bleiben leer. */
export const vorbelegung = (vorschlaege: Namensvorschlag[] | null | undefined): Zuordnung => {
    const raus: Zuordnung = {};
    (vorschlaege ?? []).forEach(v => { if (v?.vorschlag) raus[v.norm] = v.vorschlag; });
    return raus;
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) DIE UEBERNAHME
// ═══════════════════════════════════════════════════════════════════════════

export type UebernahmeMangel =
    | 'ohne_zuordnung'   // mindestens ein Gewerk des Paares ist keiner Achse zugeordnet
    | 'selbstbezug';     // beide Gewerke zeigen auf dieselbe Achse

/**
 * Was mit einer Zelle geschieht, in der schon etwas steht.
 *
 * WARUM `zusammenfuehren` und nicht `ersetzen` der Standard ist: innerhalb
 * EINER Zelle fasst diese Datei mehrere bestaetigte Bewertungen ohnehin
 * zusammen, statt eine davon zu verwerfen (siehe `uebernehmeInMatrix`). Ein
 * von Hand geschriebener Satz ist die dritte Stimme in derselben Zelle und
 * verdient dieselbe Behandlung — er ist dabei sogar die wertvollere: der
 * Katalogtext laesst sich jederzeit wiederherstellen, der handgeschriebene
 * nicht. Ersetzen bleibt waehlbar, weil ein Katalogtext eine hingeworfene
 * Notiz fachlich durchaus abloesen kann; es ist dann aber eine ausdrueckliche
 * Wahl vor sichtbarem Alttext und kein stiller Verlust.
 */
export type Konfliktloesung = 'zusammenfuehren' | 'ersetzen' | 'behalten';

/**
 * Ohne ausdrueckliche Wahl wird zusammengefuehrt. Der Standard muss der
 * verlustfreie sein: wer den Dialog bestaetigt, ohne jede Zeile zu lesen, darf
 * damit keinen Text verlieren.
 */
export const KONFLIKT_STANDARD: Konfliktloesung = 'zusammenfuehren';

/** Entscheidung je Zelle, geschluesselt mit `Zellkonflikt.schluessel`. */
export type Konfliktwahl = Record<string, Konfliktloesung>;

/** Der Inhalt einer Zelle, so wie ihn der Dialog gegenueberstellt. */
export interface Zellinhalt {
    description: string;
    measure: string;
    severity: MutualHazard['severity'];
}

/**
 * Eine Zelle, in der schon etwas steht und in die die Uebernahme schreiben
 * wuerde. Der Dialog nennt sie NAMENTLICH und zeigt `bestand` im Wortlaut —
 * eine blosse Zahl („N ersetzt") sagt niemandem, was er gleich verliert.
 */
export interface Zellkonflikt {
    /** Stabiler Schluessel — genau der, unter dem die Wahl abgelegt wird. */
    schluessel: string;
    source: string;
    affected: string;
    /** Was heute in der Matrix steht. */
    bestand: Zellinhalt;
    /** Was die Koordinierung beitraegt. */
    neu: Zellinhalt;
}

export interface UebernahmeErgebnis {
    /** Die vollstaendige neue Liste — so, wie sie an `onChange` geht. */
    hazards: MutualHazard[];
    /** Zeilen, deren `uebernommen_matrix_am` zu setzen ist. */
    uebernommenePaarIds: string[];
    /** Zellen, die es vorher nicht gab. */
    neueZellen: number;
    /** Zellen, die es schon gab und deren Text erhalten blieb. */
    zusammengefuehrteZellen: number;
    /** Zellen, deren alter Text auf ausdrueckliche Wahl hin weggeworfen wurde. */
    ersetzteZellen: number;
    /** Zellen, die auf ausdrueckliche Wahl hin unberuehrt blieben. */
    behalteneZellen: number;
    /** Belegte Zellen samt Alttext — die Vorlage fuer die Liste im Dialog. */
    konflikte: Zellkonflikt[];
    uebersprungen: { paarId: string; grund: UebernahmeMangel }[];
}

// Der Schluessel wird als JSON gebildet und nicht mit einem Trennzeichen geklebt:
// mit einem Leerzeichen fielen ("Bau Meier","Elektro") und ("Bau","Meier Elektro")
// auf denselben Schluessel zusammen.
const zellSchluessel = (source: string, affected: string): string => JSON.stringify([source, affected]);

/** Die schwerere von zwei Stufen. Unbekanntes zaehlt als „nichts". */
const schwerste = (
    a: MutualHazard['severity'],
    b: MutualHazard['severity'],
): MutualHazard['severity'] => ((SEVERITY_RANG[b] ?? 0) > (SEVERITY_RANG[a] ?? 0) ? b : a);

/**
 * Haengt neue Zeilen an einen vorhandenen Text an.
 *
 * Der Bestandstext bleibt Zeichen fuer Zeichen erhalten und steht vorn — er
 * wird nicht zerlegt, nicht gekuerzt, nicht umsortiert. Ausgelassen wird nur,
 * was bereits als eigene Zeile darin steht; sonst stuende derselbe Katalogsatz
 * nach einer zweiten Uebernahme doppelt in der Zelle.
 */
const anhaengen = (bestandText: string, neue: string[]): string => {
    const vorhandeneZeilen = bestandText.split('\n').map(z => z.trim());
    const dazu = neue.filter(n => !!n && !vorhandeneZeilen.includes(n));
    if (!dazu.length) return bestandText;
    return bestandText ? [bestandText, ...dazu].join('\n') : dazu.join('\n');
};

/**
 * Traegt die bestaetigten Paare in die Gefaehrdungsmatrix ein.
 *
 * Abbildung (unveraendert aus der Spezifikation):
 *   gewerk_a_label -> tradeSource | gewerk_b_label -> tradeAffected
 *   gefaehrdung -> description    | massnahme     -> measure
 *   prioritaet hoch|mittel|niedrig -> severity high|medium|low
 * Die Labels laufen dabei durch die Zuordnung (siehe Namensbruecke).
 *
 * EINE ZELLE ZEIGT GENAU EINEN EINTRAG — daran aendert sich nichts; zwei
 * Eintraege fuer dieselbe (tradeSource, tradeAffected) kann die Matrix nicht
 * anzeigen, `getHazard` nimmt den ersten.
 *
 * MEHRERE BEWERTUNGEN JE PAAR sind der Normalfall — der Unique-Index der
 * Tabelle laeuft ueber (project_id, a, b, GEFAEHRDUNG). Sie fallen deshalb in
 * dieselbe Zelle und werden ZUSAMMENGEFASST statt verworfen: alle
 * Gefaehrdungen untereinander, alle Massnahmen untereinander, die schwerste
 * Stufe gewinnt. Nur so verschwindet keine bestaetigte Feststellung.
 *
 * WAS IM BESTAND STEHT, FAELLT UNTER DIESELBE REGEL. Frueher warf diese
 * Funktion eine vorhandene Zelle weg (`bestand.filter(...)`), und der Dialog
 * nannte dazu nur eine Zahl — ein von Hand formulierter Satz war nach einem
 * Klick fort, ohne dass ihn jemand noch einmal gesehen haette. Jetzt gilt je
 * belegter Zelle eine Wahl:
 *  - `zusammenfuehren` (Standard): der alte Text bleibt stehen, das Neue kommt
 *    darunter, die schwerere Stufe gewinnt. Verlustfrei.
 *  - `ersetzen`: nur ausdruecklich, und der Dialog hat den Alttext vorher im
 *    Wortlaut gezeigt.
 *  - `behalten`: die Zelle bleibt, wie sie ist. Die zugehoerigen Paare gelten
 *    dann NICHT als uebernommen (kein `uebernommen_matrix_am`) — sie stehen ja
 *    nicht in der Matrix. Dass das Band sie spaeter erneut anbietet, ist der
 *    ehrliche Zustand und kein Fehler.
 *
 * Die schwerere Stufe gewinnt auch beim Zusammenfuehren: eine bestaetigte hohe
 * Bewertung darf nicht unter einer aelteren niedrigen verschwinden.
 */
export const uebernehmeInMatrix = (
    hazards: MutualHazard[] | null | undefined,
    paare: KoordinierungPaar[] | null | undefined,
    zuordnung: Zuordnung | null | undefined,
    wahl?: Konfliktwahl | null,
): UebernahmeErgebnis => {
    const bestand = Array.isArray(hazards) ? hazards : [];
    const karte = zuordnung ?? {};
    const entscheidungen = wahl ?? {};
    const kandidaten = uebernahmeKandidaten(paare);

    const uebersprungen: UebernahmeErgebnis['uebersprungen'] = [];
    const zellen = new Map<string, {
        source: string;
        affected: string;
        gefaehrdungen: string[];
        massnahmen: string[];
        severity: MutualHazard['severity'];
        paarIds: string[];
    }>();

    kandidaten.forEach(p => {
        const source = text(karte[text(p.gewerk_a_norm)]);
        const affected = text(karte[text(p.gewerk_b_norm)]);
        if (!source || !affected) {
            uebersprungen.push({ paarId: p.id, grund: 'ohne_zuordnung' });
            return;
        }
        if (source === affected) {
            // Die Matrix sperrt die Diagonale (`if (source === affected) return`
            // in handleCellClick). Ein Paar, dessen beide Gewerke auf dieselbe
            // Achse zeigen, haette dort keinen Platz — und waere ohnehin die
            // Aussage „die Firma gefaehrdet sich selbst", die in diese Matrix
            // nicht gehoert.
            uebersprungen.push({ paarId: p.id, grund: 'selbstbezug' });
            return;
        }

        const severity = PRIORITAET_ZU_SEVERITY[p.prioritaet] ?? 'medium';
        const schluessel = zellSchluessel(source, affected);
        const zelle = zellen.get(schluessel);
        if (!zelle) {
            zellen.set(schluessel, {
                source,
                affected,
                gefaehrdungen: [text(p.gefaehrdung)],
                massnahmen: text(p.massnahme) ? [text(p.massnahme)] : [],
                severity,
                paarIds: [p.id],
            });
            return;
        }
        if (!zelle.gefaehrdungen.includes(text(p.gefaehrdung))) zelle.gefaehrdungen.push(text(p.gefaehrdung));
        if (text(p.massnahme) && !zelle.massnahmen.includes(text(p.massnahme))) zelle.massnahmen.push(text(p.massnahme));
        zelle.severity = schwerste(zelle.severity, severity);
        zelle.paarIds.push(p.id);
    });

    // Der Bestand wird je Zelle gesammelt statt einzeln nachgeschlagen: es
    // koennen mehrere Zeilen auf dieselbe Zelle zeigen (`applyAiSuggestion`
    // haengt an, ohne zu ersetzen), und auch die duerfen nicht verlorengehen.
    const bestandJeZelle = new Map<string, MutualHazard[]>();
    bestand.forEach(h => {
        const schluessel = zellSchluessel(text(h?.tradeSource), text(h?.tradeAffected));
        const liste = bestandJeZelle.get(schluessel);
        if (liste) liste.push(h); else bestandJeZelle.set(schluessel, [h]);
    });

    const inhaltVonBestand = (liste: MutualHazard[]): Zellinhalt => ({
        description: liste.map(h => text(h?.description)).filter(Boolean).join('\n'),
        measure: liste.map(h => text(h?.measure)).filter(Boolean).join('\n'),
        severity: liste.reduce<MutualHazard['severity']>((s, h) => schwerste(s, h?.severity), 'low'),
    });

    const konflikte: Zellkonflikt[] = [];
    zellen.forEach((z, schluessel) => {
        const alt = bestandJeZelle.get(schluessel);
        if (!alt?.length) return;
        const bestandInhalt = inhaltVonBestand(alt);
        // Eine leere Zelle hat nichts zu verlieren und braucht keine Rueckfrage —
        // sonst stuenden im Dialog Zeilen ohne Aussage.
        if (!bestandInhalt.description && !bestandInhalt.measure) return;
        konflikte.push({
            schluessel,
            source: z.source,
            affected: z.affected,
            bestand: bestandInhalt,
            neu: {
                description: z.gefaehrdungen.join('\n'),
                measure: z.massnahmen.join('\n'),
                severity: z.severity,
            },
        });
    });
    konflikte.sort((a, b) => a.source.localeCompare(b.source, 'de') || a.affected.localeCompare(b.affected, 'de'));

    let neueZellen = 0;
    let zusammengefuehrteZellen = 0;
    let ersetzteZellen = 0;
    let behalteneZellen = 0;
    const uebernommenePaarIds: string[] = [];

    // Die Reihenfolge des Bestands bleibt erhalten: eine bearbeitete Zelle
    // bleibt an ihrem Platz, statt ans Ende der Liste zu springen.
    const neueListe: MutualHazard[] = [];
    const erledigt = new Set<string>();
    bestand.forEach(h => {
        const schluessel = zellSchluessel(text(h?.tradeSource), text(h?.tradeAffected));
        const zelle = zellen.get(schluessel);
        if (!zelle) {
            neueListe.push(h);   // nicht betroffen — dieselbe Referenz, unangetastet
            return;
        }
        const loesung = entscheidungen[schluessel] ?? KONFLIKT_STANDARD;
        if (erledigt.has(schluessel)) {
            // Weitere Altzeilen derselben Zelle: beim Behalten bleiben sie
            // stehen, sonst sind sie oben bereits eingeflossen und wuerden hier
            // ein zweites Mal dieselbe Zelle belegen.
            if (loesung === 'behalten') neueListe.push(h);
            return;
        }
        erledigt.add(schluessel);

        if (loesung === 'behalten') {
            behalteneZellen += 1;
            neueListe.push(h);
            return;
        }
        if (loesung === 'ersetzen') {
            ersetzteZellen += 1;
            // Neue id: der alte Eintrag ist weg, nicht fortgeschrieben.
            neueListe.push({
                id: crypto.randomUUID(),
                tradeSource: zelle.source,
                tradeAffected: zelle.affected,
                description: zelle.gefaehrdungen.join('\n'),
                measure: zelle.massnahmen.join('\n'),
                severity: zelle.severity,
            });
            uebernommenePaarIds.push(...zelle.paarIds);
            return;
        }

        zusammengefuehrteZellen += 1;
        const altInhalt = inhaltVonBestand(bestandJeZelle.get(schluessel) ?? [h]);
        // Die id des vorhandenen Eintrags bleibt: die Zelle wird fortgeschrieben,
        // nicht neu angelegt.
        neueListe.push({
            ...h,
            tradeSource: zelle.source,
            tradeAffected: zelle.affected,
            description: anhaengen(altInhalt.description, zelle.gefaehrdungen),
            measure: anhaengen(altInhalt.measure, zelle.massnahmen),
            severity: schwerste(altInhalt.severity, zelle.severity),
        });
        uebernommenePaarIds.push(...zelle.paarIds);
    });

    zellen.forEach((z, schluessel) => {
        if (bestandJeZelle.has(schluessel)) return;
        neueZellen += 1;
        neueListe.push({
            id: crypto.randomUUID(),
            tradeSource: z.source,
            tradeAffected: z.affected,
            description: z.gefaehrdungen.join('\n'),
            measure: z.massnahmen.join('\n'),
            severity: z.severity,
        });
        uebernommenePaarIds.push(...z.paarIds);
    });

    return {
        hazards: neueListe,
        uebernommenePaarIds,
        neueZellen,
        zusammengefuehrteZellen,
        ersetzteZellen,
        behalteneZellen,
        konflikte,
        uebersprungen,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) ZEITANGABEN FUER DIE KI
// ═══════════════════════════════════════════════════════════════════════════

/** Ein gleichzeitig taetiges Gewerk, wie es in die KI-Anfrage geht. */
export interface GleichzeitigMit {
    name: string;
    von: string;
    bis: string;
    tage: number;
}

/**
 * Was die KI ueber die Zeit wissen kann — je Achse die Gewerke, mit denen sie
 * sich nachweislich ueberschneidet, samt Fenster und Dauer.
 *
 * BEWUSST OHNE eigenen Zeitraum je Gewerk: ein Paar kennt nur das GEMEINSAME
 * Fenster, nicht die Bauzeit der einzelnen Firma. Aus Ueberschneidungen einen
 * Eigenzeitraum zurueckzurechnen hiesse, ein Datum zu erfinden — und ein
 * erfundener Zeitraum erzeugt eine erfundene Gleichzeitigkeit, die dann als
 * Feststellung des SiGeKo dastuende.
 *
 * Genommen werden alle nicht veralteten Zeilen mit vollstaendigem Fenster,
 * auch unbestaetigte: die Ueberschneidung ist gerechnet, nicht bewertet — an
 * dieser Stelle ist sie ein Terminfakt und keine Feststellung.
 */
export const zeitangabenFuerKi = (
    paare: KoordinierungPaar[] | null | undefined,
    zuordnung: Zuordnung | null | undefined,
): Record<string, GleichzeitigMit[]> => {
    const karte = zuordnung ?? {};
    const raus: Record<string, GleichzeitigMit[]> = {};

    const eintragen = (achse: string, partner: string, von: string, bis: string, tage: number): void => {
        const liste = raus[achse] ?? (raus[achse] = []);
        const vorhanden = liste.find(e => e.name === partner);
        // Mehrere Zeilen je Paar tragen dasselbe Fenster; das weiteste gewinnt,
        // damit die Angabe nicht davon abhaengt, welche Bewertung zuerst kam.
        if (!vorhanden) liste.push({ name: partner, von, bis, tage });
        else if (tage > vorhanden.tage) { vorhanden.von = von; vorhanden.bis = bis; vorhanden.tage = tage; }
    };

    (paare ?? []).forEach(p => {
        if (!p || text(p.veraltet_seit)) return;
        const von = text(p.ueberschneidung_von);
        const bis = text(p.ueberschneidung_bis);
        if (!von || !bis) return;
        const a = text(karte[text(p.gewerk_a_norm)]);
        const b = text(karte[text(p.gewerk_b_norm)]);
        if (!a || !b || a === b) return;
        const tage = typeof p.dauer_tage === 'number' && p.dauer_tage > 0 ? p.dauer_tage : 0;
        eintragen(a, b, von, bis, tage);
        eintragen(b, a, von, bis, tage);
    });

    return raus;
};
